import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ChevronLeft, Download, ExternalLink, File, FileImage, FileSpreadsheet,
  FileText, Pencil, Plus, Trash2, Upload, Video, Share2, Handshake,
  MapPin, Send, CheckSquare, Square, Link2, Ban, Folder,
} from "lucide-react";
import api from "@/lib/api";
import FileBrowser from "@/components/assets/FileBrowser";
import { saleTypeSection } from "@/lib/assetScopes";
import { AssetDiscussionsTab } from "@/components/meetings/MeetingViews";
import {
  openWhatsApp, openWhatsAppWindow, locationMessage, summaryMessage,
  fileMessage, multiFileMessage,
} from "@/lib/whatsapp";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ShareAssetDialog from "@/components/brokers/ShareAssetDialog";
import OfferedToSection from "@/components/offers/OfferedToSection";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ChangeHistory from "@/components/ChangeHistory";
import RecordTasksTab from "@/components/RecordTasksTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import QuickNoteButton from "@/components/QuickNoteButton";
import TonalBadge from "@/components/TonalBadge";
import { formatINR, formatINRCompact, formatNumber, formatDate, formatDateTime, dueLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useMasterList } from "@/hooks/useMasterData";

const ALLOWED_DOC_EXTS = ["jpg", "jpeg", "png", "pdf", "doc", "docx", "xlsx"];
const MAX_BYTES = 25 * 1024 * 1024;

function iconFor(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return FileImage;
  if (["mp4", "mov", "webm"].includes(ext)) return Video;
  if (ext === "pdf") return FileText;
  if (["xlsx", "xls", "csv"].includes(ext)) return FileSpreadsheet;
  return File;
}

function MediaTile({ assetId, f, onDelete, canDelete, canShare, shareMode, isSelected, onToggleSelect, onShareFile }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = (f.content_type || "").startsWith("image/");
  useEffect(() => {
    let url;
    let active = true;
    if (isImage) {
      api.get(`/assets/${assetId}/files/${f.id}/view`, { responseType: "blob" }).then((res) => {
        if (!active) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      }).catch(() => {});
    }
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId, f.id, isImage]);

  const item = { id: f.id, name: f.original_filename, kind: "media" };

  return (
    <div className={`relative group aspect-video bg-slate-100 rounded-md overflow-hidden border ${isSelected ? "border-[#B8935A] ring-2 ring-[#B8935A]" : "border-slate-200"}`} data-testid={`media-tile-${f.id}`}>
      {isImage ? (
        blobUrl ? (
          <img src={blobUrl} alt={f.original_filename} className="w-full h-full object-cover" />
        ) : (
          <Skeleton className="w-full h-full" />
        )
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
          <Video className="h-6 w-6" />
          <div className="text-[10px] mt-1 truncate px-2">{f.original_filename}</div>
        </div>
      )}
      {/* Always-visible share controls (mobile-safe; not hover-dependent) */}
      {(shareMode || canShare) && (
        <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1.5">
          {shareMode && (
            <button
              type="button"
              onClick={() => onToggleSelect(item)}
              className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-md bg-white/90 shadow border border-slate-200"
              data-testid={`media-select-${f.id}`}
              aria-label="Select media"
            >
              {isSelected ? <CheckSquare className="h-5 w-5 text-[#B8935A]" /> : <Square className="h-5 w-5 text-slate-500" />}
            </button>
          )}
          {!shareMode && canShare && (
            <button
              type="button"
              onClick={() => onShareFile(item)}
              className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-md bg-emerald-600 text-white shadow hover:bg-emerald-700"
              data-testid={`media-whatsapp-${f.id}`}
              aria-label="Share media on WhatsApp"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2 gap-1.5">
        <div className="text-[10px] text-white font-mono-num truncate">{f.original_filename}</div>
        <div className="flex gap-1">
          <Button
            size="sm" variant="secondary" className="h-7 text-[11px] flex-1"
            onClick={async () => {
              const res = await api.get(`/assets/${assetId}/files/${f.id}/download`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url; a.download = f.original_filename; a.click();
              URL.revokeObjectURL(url);
            }}
            data-testid={`media-download-${f.id}`}
          >
            <Download className="h-3 w-3 mr-1" /> Download
          </Button>
          {canDelete && (
            <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => onDelete(f.id)} data-testid={`media-delete-${f.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-slate-100 last:border-0">
      <div className="label-eyebrow !text-slate-500 pt-0.5">{label}</div>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}

function useLabel(listType, id) {
  const { data = [] } = useMasterList(listType);
  return useMemo(() => data.find((d) => d.id === id)?.label || "—", [data, id]);
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const canBrokers = roles.includes("admin") || roles.includes("relationship_manager") || roles.includes("sourcing_manager");
  const isSMOnly = roles.includes("sourcing_manager") && !roles.includes("admin") && !roles.includes("relationship_manager");
  const canShareFiles = roles.includes("admin") || roles.includes("relationship_manager");

  const [shareMode, setShareMode] = useState(false);
  const [selected, setSelected] = useState({});

  const { data: a, isLoading } = useQuery({
    queryKey: ["asset", id],
    queryFn: async () => (await api.get(`/assets/${id}`)).data,
  });

  const { data: files = [] } = useQuery({
    queryKey: ["asset-files", id],
    queryFn: async () => (await api.get(`/assets/${id}/files`)).data,
    enabled: !!a,
  });

  const mediaFiles = useMemo(() => files.filter((f) => f.file_kind === "media"), [files]);
  const docFiles = useMemo(() => files.filter((f) => f.file_kind === "document"), [files]);

  const category = useLabel("asset_category", a?.asset_category_id);
  const type = useLabel("asset_type", a?.asset_type_id);
  const priceUnit = useLabel("price_unit", a?.offer_price_unit_id);
  const areaUnit = useLabel("area_unit", a?.area_unit_id);
  const builtUpAreaUnit = useLabel("area_unit", a?.built_up_area_unit);
  const city = useLabel("city", a?.city_id);
  const village = useLabel("village_town", a?.village_id);
  const saleType = useLabel("sale_type", a?.sale_type_id);
  const bank = useLabel("bank_arc", a?.bank_arc_id);
  const possession = useLabel("possession_type", a?.possession_type_id);
  const caseForum = useLabel("case_forum", a?.case_forum_id);
  const emdUnit = useLabel("price_unit", a?.emd_amount_unit_id);
  const { data: saleTypeOpts = [] } = useMasterList("sale_type");
  const isLand = saleTypeSection(saleTypeOpts.find((s) => s.id === a?.sale_type_id)) === "resale";
  const { data: docTypes = [] } = useMasterList("document_type");

  const shareAsset = useMemo(() => ({
    name: a?.name,
    asset_code: a?.asset_code,
    city_label: city,
    village_label: village,
    category_label: category,
    type_label: type,
    sale_type_label: saleType,
    asset_address: a?.asset_address,
    offer_price_inr: a?.offer_price_inr,
    area_sqft: a?.area_sqft,
  }), [a, city, village, category, type, saleType]);

  const selectedList = Object.values(selected);
  const toggleSelect = (item) => setSelected((p) => {
    const n = { ...p };
    if (n[item.id]) delete n[item.id];
    else n[item.id] = item;
    return n;
  });

  const shareFiles = async (items, isMulti) => {
    const w = window.open("", "_blank"); // sync open within click gesture (avoids popup block)
    try {
      const ids = items.map((f) => f.id).slice(0, 10);
      const res = await api.post(`/assets/${id}/share-link`, { file_ids: ids });
      const links = res.data?.links || [];
      if (!links.length) {
        if (w) w.close();
        toast.error("Could not generate share link");
        return;
      }
      qc.invalidateQueries({ queryKey: ["asset-share-links", id] });
      if (isMulti && links.length > 1) {
        openWhatsAppWindow(w, multiFileMessage(shareAsset, links));
      } else {
        const l = links[0];
        const meta = items.find((it) => it.id === l.file_id) || items[0];
        openWhatsAppWindow(w, fileMessage(shareAsset, l.file_name, l.url, meta.kind === "media"));
      }
      setShareMode(false);
      setSelected({});
    } catch (e) {
      if (w) w.close();
      toast.error(e?.response?.data?.detail || "Share failed");
    }
  };

  const { data: assetStatusOptions = [] } = useMasterList("asset_status");
  const statusMut = useMutation({
    mutationFn: async (newStatus) => api.patch(`/assets/${id}/status`, { status: newStatus }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset", id] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast.success("Status updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/assets/${id}`),
    onSuccess: () => {
      toast.success("Asset deleted");
      navigate("/assets");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const uploadFile = async (file, fileKind, documentName = null) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (fileKind === "document" && !ALLOWED_DOC_EXTS.includes(ext)) {
      toast.error(`File type .${ext} not allowed. Accept: ${ALLOWED_DOC_EXTS.join(", ")}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 25MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("file_kind", fileKind);
    if (documentName) fd.append("document_name", documentName);
    try {
      await api.post(`/assets/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["asset-files", id] });
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/assets/${id}/files/${fileId}`);
    qc.invalidateQueries({ queryKey: ["asset-files", id] });
    toast.success("Deleted");
  };

  if (isLoading || !a) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-32 w-full max-w-4xl" />
        <Skeleton className="h-64 w-full max-w-4xl" />
      </div>
    );
  }

  const emdDue = dueLabel(a.emd_due_date);
  const auctionDue = dueLabel(a.auction_date);
  const bidDue = dueLabel(a.bid_submission_date);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-20 max-w-6xl">
      <button
        type="button"
        onClick={() => navigate("/assets")}
        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="back-to-list"
      >
        <ChevronLeft className="h-3 w-3" /> Back to Assets
      </button>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-mono-num text-xs text-slate-500" data-testid="asset-code">{a.asset_code}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1 break-words" data-testid="asset-name">{a.name}</h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <StatusBadge status={a.status} />
            <span className="text-xs text-slate-500">
              Created by <span className="font-semibold text-slate-700">{a.created_by_name}</span> · {formatDateTime(a.created_at)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0">
          <Button
            variant="outline"
            className="min-h-[48px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => openWhatsApp(summaryMessage(shareAsset))}
            data-testid="share-asset-summary-btn"
          >
            <Send className="h-4 w-4 mr-1.5" /> Share Asset
          </Button>
          {isAdmin && (
            <Select value={a.status} onValueChange={(v) => statusMut.mutate(v)}>
              <SelectTrigger className="w-[170px]" data-testid="status-change-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assetStatusOptions.map((s) => (
                  <SelectItem key={s.id} value={s.label} data-testid={`status-option-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => navigate(`/assets/${id}/edit`)} data-testid="edit-asset-button">
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
          <QuickNoteButton recordType="Asset" recordId={id} recordName={a.name} />
          {isAdmin && (
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => {
                if (window.confirm("Delete this asset? This cannot be undone.")) deleteMut.mutate();
              }}
              data-testid="delete-asset-button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="label-eyebrow">Offer Price</div>
          <div className="font-mono-num text-lg font-bold text-slate-900 mt-1">{formatINRCompact(a.offer_price_inr)}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{a.offer_price} {priceUnit}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="label-eyebrow">Area</div>
          <div className="font-mono-num text-lg font-bold text-slate-900 mt-1">{formatNumber(Math.round(a.area_sqft))} <span className="text-xs text-slate-500">sqft</span></div>
          <div className="text-[11px] text-slate-500 mt-0.5">{a.area} {areaUnit}</div>
        </div>
        {isLand ? (
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="label-eyebrow">Possession</div>
            <div className="text-lg font-bold text-slate-900 mt-1">{possession || "—"}</div>
          </div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="label-eyebrow">Auction Date</div>
              <div className="font-mono-num text-lg font-bold text-slate-900 mt-1">{formatDate(a.auction_date)}</div>
              {auctionDue && <div className={`text-[11px] mt-0.5 font-semibold ${auctionDue.tone === "critical" ? "text-red-700" : auctionDue.tone === "warning" ? "text-amber-700" : "text-slate-500"}`}>{auctionDue.text}</div>}
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="label-eyebrow">EMD Due</div>
              <div className="font-mono-num text-lg font-bold text-slate-900 mt-1">{formatDate(a.emd_due_date)}</div>
              {emdDue && <div className={`text-[11px] mt-0.5 font-semibold ${emdDue.tone === "critical" ? "text-red-700" : emdDue.tone === "warning" ? "text-amber-700" : "text-slate-500"}`}>{emdDue.text}</div>}
            </div>
          </>
        )}
      </div>

      {/* Engagement tabs: Matched Leads (M3), Shared with Brokers (M7), Offered To (M8) */}
      <div className="mt-8" data-testid="asset-engagement-tabs">
        <Tabs defaultValue="matched">
          <TabsList className="bg-slate-100 w-full justify-start overflow-x-auto">
            <TabsTrigger value="matched" data-testid="tab-matched-leads">Matched Leads</TabsTrigger>
            {canBrokers && <TabsTrigger value="brokers" data-testid="tab-shared-brokers">Shared with Brokers</TabsTrigger>}
            {canBrokers && !isSMOnly && <TabsTrigger value="broker-discussions" data-testid="tab-broker-discussions">Broker Discussions</TabsTrigger>}
            {canBrokers && <TabsTrigger value="offered" data-testid="tab-offered-to">Offered To</TabsTrigger>}
            <TabsTrigger value="changes" data-testid="tab-change-history">Change History</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="matched">
            <MatchedLeadsCard assetId={id} />
          </TabsContent>
          {canBrokers && (
            <TabsContent value="brokers">
              <SharedBrokersCard assetId={id} />
            </TabsContent>
          )}

          {canBrokers && !isSMOnly && (
            <TabsContent value="broker-discussions">
              <AssetDiscussionsTab assetId={id} />
            </TabsContent>
          )}
          {canBrokers && (
            <TabsContent value="offered">
              <OfferedToSection assetId={id} showClients={!isSMOnly} />
            </TabsContent>
          )}
          <TabsContent value="changes">
            <ChangeHistory entityType="Asset" entityId={id} />
          </TabsContent>
          <TabsContent value="tasks">
            <RecordTasksTab recordType="Asset" recordId={id} recordName={a.name} />
          </TabsContent>
        </Tabs>
      </div>

      {!isSMOnly && <TopMatchingLeads assetId={id} />}

      {/* Sections */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="label-eyebrow">Basic Details</div>
              <Button
                size="sm"
                variant="outline"
                className="min-h-[48px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => openWhatsApp(locationMessage(shareAsset))}
                data-testid="share-location-btn"
              >
                <MapPin className="h-4 w-4 mr-1.5" /> Share Location
              </Button>
            </div>
            <div className="mt-2">
              <FieldRow label="Category">{category}</FieldRow>
              <FieldRow label="Type">{type}</FieldRow>
              <FieldRow label="Offer Price"><span className="font-mono-num">{formatINR(a.offer_price_inr)}</span> <span className="text-xs text-slate-500">({a.offer_price} {priceUnit})</span></FieldRow>
              <FieldRow label="Area"><span className="font-mono-num">{formatNumber(Math.round(a.area_sqft))} sqft</span> <span className="text-xs text-slate-500">({a.area} {areaUnit})</span></FieldRow>
              {a.built_up_area != null && a.built_up_area !== "" && (
                <FieldRow label="Built Up Area"><span className="font-mono-num">{a.built_up_area}</span> <span className="text-xs text-slate-500">{builtUpAreaUnit}</span></FieldRow>
              )}
              <FieldRow label="Address"><div className="whitespace-pre-line">{a.asset_address}</div></FieldRow>
              <FieldRow label="City / Village">{city} · {village}</FieldRow>
              {a.google_maps_link && (
                <FieldRow label="Map">
                  <a href={a.google_maps_link} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">
                    Open in Maps <ExternalLink className="h-3 w-3" />
                  </a>
                </FieldRow>
              )}
              {a.dues != null && <FieldRow label="Dues"><span className="font-mono-num">{formatINR(a.dues)}</span></FieldRow>}
              {a.short_description && <FieldRow label="Description"><div className="whitespace-pre-line">{a.short_description}</div></FieldRow>}
            </div>
          </div>

          {isLand ? (
            <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="asset-pricing-details">
              <div className="label-eyebrow">Pricing & Details</div>
              <div className="mt-2">
                <FieldRow label="Sale Type">{saleType}</FieldRow>
                <FieldRow label="Offer Price"><span className="font-mono-num">{formatINR(a.offer_price_inr)}</span> <span className="text-xs text-slate-500">({a.offer_price} {priceUnit})</span></FieldRow>
                <FieldRow label="Possession">{possession}</FieldRow>
                {a.borrower_name && <FieldRow label="Owner Name">{a.borrower_name}</FieldRow>}
                {a.asset_history && <FieldRow label="Asset Details"><div className="whitespace-pre-line">{a.asset_history}</div></FieldRow>}
              </div>
            </div>
          ) : (
          <>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="label-eyebrow">Sale & Legal Details</div>
            <div className="mt-2">
              <FieldRow label="Sale Type">{saleType}</FieldRow>
              <FieldRow label="Bank / ARC">{bank}</FieldRow>
              <FieldRow label="Possession">{possession}</FieldRow>
              {a.borrower_name && <FieldRow label="Borrower">{a.borrower_name}</FieldRow>}
              <FieldRow label="Case Forum">{caseForum}</FieldRow>
              {a.asset_history && <FieldRow label="History"><div className="whitespace-pre-line">{a.asset_history}</div></FieldRow>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="label-eyebrow">Sale Process & Critical Dates</div>
            <div className="mt-2">
              <FieldRow label="Auction Date"><span className="font-mono-num">{formatDate(a.auction_date)}</span></FieldRow>
              {a.emd_amount != null && (
                <FieldRow label="EMD Amount">
                  <span className="font-mono-num">{formatINR(a.emd_amount_inr)}</span>
                  <span className="text-xs text-slate-500"> ({a.emd_amount} {emdUnit})</span>
                </FieldRow>
              )}
              {a.emd_due_date && <FieldRow label="EMD Due Date"><span className="font-mono-num">{formatDate(a.emd_due_date)}</span></FieldRow>}
              {a.bid_submission_date && (
                <FieldRow label="Bid Submission">
                  <span className="font-mono-num">{formatDate(a.bid_submission_date)}</span>
                  {bidDue && <span className={`ml-2 text-xs font-semibold ${bidDue.tone === "critical" ? "text-red-700" : "text-slate-500"}`}>{bidDue.text}</span>}
                </FieldRow>
              )}
              {a.emd_payment_instructions && (
                <FieldRow label="EMD Instructions"><div className="whitespace-pre-line">{a.emd_payment_instructions}</div></FieldRow>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Documents checklist */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="label-eyebrow">Documents Available</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(a.documents_available || []).length === 0 ? (
                <div className="text-xs text-slate-500">None marked.</div>
              ) : (
                (a.documents_available || []).map((dId) => {
                  const d = docTypes.find((x) => x.id === dId);
                  return (
                    <span key={dId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 border border-slate-200 rounded">
                      <FileText className="h-3 w-3" />
                      {d?.label || dId}
                    </span>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Unified folder-based File Browser (replaces Asset Media + Asset Documents) */}
      <FileBrowser
        assetId={id}
        asset={a}
        shareAsset={shareAsset}
        canShare={canShareFiles}
        isAdmin={isAdmin}
        userId={user.id}
      />

      {/* Shared Links panel (Admin/RM only) */}
      {canShareFiles && <SharedLinksCard assetId={id} />}

    </div>
  );
}

function SharedBrokersCard({ assetId }) {
  const [showShare, setShowShare] = useState(false);
  const { data: shares = [] } = useQuery({
    queryKey: ["asset-shares", assetId],
    queryFn: async () => (await api.get("/brokers/shares", { params: { asset_id: assetId } })).data,
  });
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="shared-brokers-card">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow flex items-center gap-1.5"><Handshake className="h-3.5 w-3.5" /> Shared with Brokers ({shares.length})</div>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setShowShare(true)} data-testid="asset-share-btn"><Share2 className="h-3.5 w-3.5 mr-1.5" />Share with Brokers</Button>
      </div>
      {shares.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3">Not shared with any broker yet.</div>
      ) : (
        <table className="data-table w-full mt-3">
          <thead><tr><th className="label-eyebrow !text-slate-600">Broker</th><th className="label-eyebrow !text-slate-600">Shared</th><th className="label-eyebrow !text-slate-600">Exclusivity</th><th className="label-eyebrow !text-slate-600">Status</th></tr></thead>
          <tbody>
            {shares.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`asset-share-row-${s.id}`}>
                <td className="text-sm font-medium text-slate-900">{s.broker_name}</td>
                <td className="text-sm text-slate-600 font-mono-num">{formatDate(s.shared_at)}</td>
                <td className="text-sm text-slate-600">{s.exclusivity_type}</td>
                <td><TonalBadge listType="broker_share_status" label={s.share_status_label} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ShareAssetDialog open={showShare} onClose={() => setShowShare(false)} assetId={assetId} />
    </div>
  );
}

function DocumentRows({ assetId, existing, onUpload, onDelete, canDelete, canShare, shareMode, selected, onToggleSelect, onShareFile }) {
  // Start with 3 blank rows; "+ Add document" appends one more each click (no upper limit).
  const [blankCount, setBlankCount] = useState(3);
  const rows = useMemo(() => {
    return [
      ...existing.map((f) => ({ kind: "existing", file: f })),
      ...Array.from({ length: blankCount }).map((_, i) => ({ kind: "blank", index: i })),
    ];
  }, [existing, blankCount]);

  const [pendingNames, setPendingNames] = useState({}); // index -> name string

  return (
    <>
      <div className="mt-4 divide-y divide-slate-100">
      {rows.map((row, idx) => (
        <div key={row.kind === "existing" ? row.file.id : `blank-${row.index}`} className="flex items-center gap-3 py-2.5">
          {row.kind === "existing" ? (
            <>
              {shareMode && canShare && (
                <button
                  type="button"
                  onClick={() => onToggleSelect({ id: row.file.id, name: row.file.original_filename, kind: "document" })}
                  className="min-h-[48px] min-w-[48px] flex items-center justify-center shrink-0"
                  data-testid={`doc-select-${row.file.id}`}
                  aria-label="Select document"
                >
                  {selected && selected[row.file.id] ? <CheckSquare className="h-5 w-5 text-[#B8935A]" /> : <Square className="h-5 w-5 text-slate-400" />}
                </button>
              )}
              {(() => {
                const Icon = iconFor(row.file.original_filename);
                return <Icon className="h-4 w-4 text-slate-500 shrink-0" />;
              })()}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate" data-testid={`doc-name-${row.file.id}`}>
                  {row.file.document_name || row.file.original_filename}
                </div>
                <div className="text-xs text-slate-500">
                  {row.file.original_filename} · {(row.file.size / 1024).toFixed(0)} KB · {formatDate(row.file.created_at)}
                </div>
              </div>
              {canShare && !shareMode && (
                <Button
                  size="sm" variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 min-h-[48px] min-w-[48px]"
                  onClick={() => onShareFile({ id: row.file.id, name: row.file.original_filename, kind: "document" })}
                  data-testid={`doc-whatsapp-${row.file.id}`}
                  aria-label="Share document on WhatsApp"
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                size="sm" variant="outline"
                onClick={async () => {
                  const res = await api.get(`/assets/${assetId}/files/${row.file.id}/download`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = row.file.original_filename; a.click();
                  URL.revokeObjectURL(url);
                }}
                data-testid={`doc-download-${row.file.id}`}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              {canDelete && (
                <Button
                  size="sm" variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={() => onDelete(row.file.id)}
                  data-testid={`doc-delete-${row.file.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          ) : (
            <>
              <File className="h-4 w-4 text-slate-300 shrink-0" />
              <Input
                value={pendingNames[row.index] || ""}
                onChange={(e) => setPendingNames((p) => ({ ...p, [row.index]: e.target.value }))}
                placeholder="Document name (e.g. Title Deed - Plot 12)"
                className="flex-1 h-9"
                data-testid={`doc-row-name-${row.index}`}
              />
              <label className="cursor-pointer">
                <Button asChild size="sm" variant="outline">
                  <span>
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xlsx,application/pdf,image/jpeg,image/png"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      onUpload(f, "document", pendingNames[row.index] || null);
                      setPendingNames((p) => ({ ...p, [row.index]: "" }));
                    }
                    e.target.value = "";
                  }}
                  data-testid={`doc-row-file-${row.index}`}
                />
              </label>
            </>
          )}
        </div>
      ))}
      </div>
      <div className="mt-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setBlankCount((c) => c + 1)}
          data-testid="add-document-row-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add document
        </Button>
      </div>
    </>
  );
}


function SharedLinksCard({ assetId }) {
  const qc = useQueryClient();
  const { data: links = [], isLoading } = useQuery({
    queryKey: ["asset-share-links", assetId],
    queryFn: async () => (await api.get(`/assets/${assetId}/share-links`)).data,
  });

  const revokeMut = useMutation({
    mutationFn: async (token) => api.post(`/assets/${assetId}/share-links/${token}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset-share-links", assetId] });
      toast.success("Link revoked");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy")
    );
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md p-5" data-testid="shared-links-card">
      <div className="label-eyebrow flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Shared Links ({links.length})</div>
      <div className="text-xs text-slate-500 mt-0.5">Permanent public file links. Revoke any time — revoked links stop working immediately.</div>
      {isLoading ? (
        <div className="mt-3 space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : links.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3">No links shared yet. Use "Select to share" on Media or Documents.</div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="label-eyebrow !text-slate-600">Type</th>
                <th className="label-eyebrow !text-slate-600">Name</th>
                <th className="label-eyebrow !text-slate-600">Shared by</th>
                <th className="label-eyebrow !text-slate-600">When</th>
                <th className="label-eyebrow !text-slate-600">Status</th>
                <th className="label-eyebrow !text-slate-600 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.token} className="border-t border-slate-100" data-testid={`share-link-row-${l.token}`}>
                  <td className="text-sm" data-testid={`share-link-type-${l.token}`}>
                    {l.link_type === "folder"
                      ? <span className="inline-flex items-center gap-1 text-slate-700"><Folder className="h-3.5 w-3.5 text-[#B8935A]" /> Folder</span>
                      : <span className="inline-flex items-center gap-1 text-slate-700"><FileText className="h-3.5 w-3.5 text-slate-500" /> File</span>}
                  </td>
                  <td className="text-sm font-medium text-slate-900 max-w-[200px] truncate" data-testid={`share-link-file-${l.token}`}>{l.file_name}</td>
                  <td className="text-sm text-slate-600">{l.created_by_name || "—"}</td>
                  <td className="text-sm text-slate-600 font-mono-num">{formatDateTime(l.created_at)}</td>
                  <td>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${l.status === "Revoked" ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                      data-testid={`share-link-status-${l.token}`}
                    >
                      {l.status}
                    </span>
                    {l.status === "Revoked" && l.revoked_by_name && (
                      <div className="text-[10px] text-slate-400 mt-0.5">by {l.revoked_by_name}</div>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      {l.status === "Active" && (
                        <>
                          <Button size="sm" variant="outline" className="min-h-[40px]" onClick={() => copyLink(l.url)} data-testid={`share-link-copy-${l.token}`}>
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 min-h-[40px]"
                            onClick={() => { if (window.confirm("Revoke this link? Anyone with it will lose access.")) revokeMut.mutate(l.token); }}
                            data-testid={`share-link-revoke-${l.token}`}
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function MatchedLeadsCard({ assetId }) {
  const { data: matches = [] } = useQuery({
    queryKey: ["asset-matches", assetId],
    queryFn: async () => (await api.get(`/matches?asset_id=${assetId}&archive=true`)).data,
  });
  if (!matches.length) {
    return (
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="label-eyebrow">Matched Leads</div>
        <div className="text-sm text-slate-500 mt-2">No matches yet. Active leads with matching requirements will auto-appear here.</div>
      </div>
    );
  }
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="matched-leads-card">
      <div className="label-eyebrow">Matched Leads ({matches.length})</div>
      <div className="mt-3 divide-y divide-slate-100">
        {matches.sort((a, b) => b.match_score - a.match_score).map((m) => (
          <div key={m.id} className="py-3 flex items-center gap-3" data-testid={`matched-lead-row-${m.id}`}>
            <div className={`font-mono-num text-lg font-bold w-12 ${m.match_score >= 75 ? "text-emerald-700" : m.match_score >= 50 ? "text-amber-700" : "text-slate-600"}`}>
              {m.match_score}%
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/leads/${m.lead_id}`} className="text-sm font-semibold text-slate-900 hover:underline">{m.lead_name}</Link>
              <div className="text-[11px] text-slate-500 font-mono-num">{m.lead_code} · {m.lead_owner_name}</div>
            </div>
            <TonalBadge listType="match_status" label={m.match_status_label} />
            <Link to="/kanban" className="text-xs font-semibold text-slate-700 hover:underline">Open</Link>
          </div>
        ))}
      </div>
    </div>
  );
}


function scoreTone(score) {
  if (score >= 75) return "text-emerald-700";
  if (score >= 50) return "text-amber-700";
  return "text-slate-600";
}

// Live recommendation list — this Asset scored against all open leads (Module 2
// reciprocal of the Lead page's "Top 5 Matching Assets"). Independent of pipeline.
function TopMatchingLeads({ assetId }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["asset-matching-leads", assetId],
    queryFn: async () => (await api.get(`/assets/${assetId}/matching-leads`)).data,
    staleTime: 60_000,
  });
  const matches = data?.matches || [];

  if (isLoading) {
    return (
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="top-matching-leads-card">
        <div className="label-eyebrow">Top Matching Leads</div>
        <div className="mt-3 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      </div>
    );
  }
  if (matches.length === 0) return null;

  const visible = matches.slice(0, 4);
  const row = (m) => (
    <Link
      key={m.id}
      to={`/leads/${m.id}`}
      className="py-3 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 rounded"
      data-testid={`top-matching-lead-${m.id}`}
    >
      <div className={`font-mono-num text-lg font-bold w-12 ${scoreTone(m.match_score)}`}>{m.match_score}%</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">{m.lead_name}</div>
        <div className="text-[11px] text-slate-500 truncate">{m.company_name || m.lead_code}</div>
      </div>
      <TonalBadge listType="lead_status" id={m.lead_status_id} />
    </Link>
  );

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="top-matching-leads-card">
      <div className="label-eyebrow">Top Matching Leads</div>
      <div className="text-[11px] text-slate-500 mt-1">Live recommendations — open leads ranked against this asset, independent of pipeline.</div>
      <div className="mt-3 divide-y divide-slate-100">
        {visible.map(row)}
      </div>
      {matches.length > 4 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 text-xs font-semibold text-[#B8935A] hover:underline"
          data-testid="top-matching-leads-see-more"
        >
          See more ({matches.length})
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="top-matching-leads-dialog">
          <DialogHeader>
            <DialogTitle>Top Matching Leads ({matches.length})</DialogTitle>
            <DialogDescription className="sr-only">Open leads ranked by match score against this asset.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
            {open && matches.map(row)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
